import React from "react";
import { Container, Typography, Box } from "@mui/material";
import { motion } from "framer-motion";

const HotspotShowcase = () => {
    return (
        <Box
            sx={{
                backgroundColor: '#ffffff',
                py: { xs: 10, md: 18 },
            }}
            id="hotspot-landing-page"
        >
            <Container maxWidth="lg">
                <motion.div
                    initial={{ opacity: 0, y: 30 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true, margin: '-80px' }}
                    transition={{ duration: 0.8, ease: [0.25, 0.1, 0.25, 1] }}
                >
                    <Typography
                        variant="h2"
                        align="center"
                        sx={{
                            fontWeight: 700,
                            letterSpacing: '-0.03em',
                            background: 'linear-gradient(135deg, #19b594 0%, #0b74c1 100%)',
                            WebkitBackgroundClip: 'text',
                            WebkitTextFillColor: 'transparent',
                            backgroundClip: 'text',
                            mb: 2,
                        }}
                    >
                        Experience Our Hotspot Landing Page
                    </Typography>
                    <Typography
                        sx={{
                            textAlign: 'center',
                            color: 'rgba(29,29,31,0.65)',
                            fontSize: 'clamp(1.05rem, 0.3vw + 0.5rem, 1.25rem)',
                            maxWidth: '600px',
                            mx: 'auto',
                            mb: { xs: 6, md: 10 },
                            lineHeight: 1.6,
                        }}
                    >
                        See how your customers will experience the NasRadius hotspot portal.
                    </Typography>
                </motion.div>

                <Box
                    sx={{
                        display: 'flex',
                        flexDirection: { xs: 'column', md: 'row' },
                        gap: { xs: 6, md: 8, lg: 10 },
                        alignItems: { xs: 'center', md: 'flex-start' },
                        justifyContent: 'center',
                    }}
                >
                    <motion.div
                        initial={{ opacity: 0, y: 40 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true, margin: '-50px' }}
                        transition={{ duration: 0.8, delay: 0.1, ease: [0.25, 0.1, 0.25, 1] }}
                        style={{ flex: '1 1 50%', maxWidth: '100%', textAlign: 'center' }}
                    >
                        <Typography
                            variant="h5"
                            sx={{
                                fontWeight: 600,
                                color: '#1d1d1f',
                                letterSpacing: '-0.015em',
                                mb: { xs: 2, md: 3 },
                            }}
                        >
                            Watch How It Works
                        </Typography>
                        <Box
                            sx={{
                                display: 'flex',
                                justifyContent: 'center',
                                alignItems: 'center',
                                width: '100%',
                                maxWidth: { xs: '100%', sm: '300px', md: '340px' },
                                margin: '0 auto',
                                backgroundColor: '#000',
                                borderRadius: { xs: 16, sm: 20 },
                                p: { xs: 0.5, sm: 0.75 },
                                boxShadow: '0 24px 64px rgba(0, 0, 0, 0.35)',
                            }}
                        >
                            <Box
                                sx={{
                                    width: '100%',
                                    borderRadius: { xs: 14, sm: 18 },
                                    overflow: 'hidden',
                                    backgroundColor: '#000',
                                }}
                            >
                                <video
                                    src="/videos/hotspot_demo_video.mp4"
                                    controls
                                    autoPlay
                                    loop
                                    muted
                                    playsInline
                                    loading="lazy"
                                    style={{
                                        width: '100%',
                                        height: {xs: '600px', sm:'600px', md: 'auto'},
                                        display: 'block',
                                        outline: 'none',
                                    }}
                                >
                                    Your browser does not support the video tag.
                                </video>
                            </Box>
                        </Box>
                    </motion.div>

                    <motion.div
                        initial={{ opacity: 0, y: 40 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true, margin: '-50px' }}
                        transition={{ duration: 0.8, delay: 0.25, ease: [0.25, 0.1, 0.25, 1] }}
                        style={{ flex: '1 1 50%', maxWidth: '100%', textAlign: 'center' }}
                    >
                        <Typography
                            variant="h5"
                            sx={{
                                fontWeight: 600,
                                color: '#1d1d1f',
                                letterSpacing: '-0.015em',
                                mb: { xs: 2, md: 3 },
                            }}
                        >
                            Try It Live
                        </Typography>

                        <Box
                            sx={{
                                width: { xs: '320px', sm: '360px', md: '400px' },
                                maxWidth: '400px',
                                height: { xs: '580px', sm: '700px', md: '750px' },
                                display: { xs: 'block', md: 'none' },
                                borderRadius: 3,
                                overflow: 'hidden',
                                backgroundColor: '#fff',
                                border: '1px solid rgba(0,0,0,0.08)',
                                boxShadow: '0 16px 48px rgba(0,0,0,0.12)',
                                mx: 'auto',
                            }}
                        >
                            <iframe
                                src="https://r.nasradius.com"
                                title="Hotspot Landing Page"
                                style={{
                                    width: '100%',
                                    height: '100%',
                                    border: 'none',
                                }}
                                loading="lazy"
                                allow="fullscreen"
                            />
                        </Box>

                        <Box
                            sx={{
                                width: { sm: '320px', md: '400px' },
                                maxWidth: '400px',
                                aspectRatio: '9/16',
                                display: { xs: 'none', md: 'block' },
                                backgroundColor: '#000',
                                borderRadius: 20,
                                p: 1,
                                boxShadow: '0 24px 64px rgba(0, 0, 0, 0.3)',
                                position: 'relative',
                                mx: 'auto',
                            }}
                        >
                            <Box
                                sx={{
                                    position: 'absolute',
                                    top: 14,
                                    left: '50%',
                                    transform: 'translateX(-50%)',
                                    width: '120px',
                                    height: '20px',
                                    backgroundColor: '#000',
                                    borderRadius: '0 0 12px 12px',
                                    zIndex: 2,
                                }}
                            />
                            <Box
                                sx={{
                                    width: '100%',
                                    height: '100%',
                                    borderRadius: 16,
                                    overflow: 'hidden',
                                    backgroundColor: '#fff',
                                }}
                            >
                                <iframe
                                    src="https://r.nasradius.com"
                                    title="Hotspot Landing Page"
                                    style={{
                                        width: '100%',
                                        height: '100%',
                                        border: 'none',
                                    }}
                                    loading="lazy"
                                    scrolling="yes"
                                    allow="fullscreen"
                                />
                            </Box>
                        </Box>
                    </motion.div>
                </Box>
            </Container>
        </Box>
    );
};

export default HotspotShowcase;
